import type {
  CreateTicketFeaturePayload,
  TicketFeatureListParams,
  UpdateTicketFeaturePayload,
} from '@/shared/api/types/ticket-feature-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createTicketFeature,
  getTicketFeatures,
  toggleTicketFeatureActive,
  updateTicketFeature,
} from '@/shared/api/services/ticket-feature-service'

export const TICKET_FEATURE_QUERY_KEYS = {
  all: ['ticket-features'] as const,
  listPrefix: () => [...TICKET_FEATURE_QUERY_KEYS.all, 'list'] as const,
  list: (params: TicketFeatureListParams) =>
    [...TICKET_FEATURE_QUERY_KEYS.all, 'list', params] as const,
}

export function useTicketFeaturesQuery(params: TicketFeatureListParams) {
  return useQuery({
    queryKey: TICKET_FEATURE_QUERY_KEYS.list(params),
    queryFn: async () => getTicketFeatures(params),
    staleTime: 0,
  })
}

export function useCreateTicketFeatureMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateTicketFeaturePayload) => createTicketFeature(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TICKET_FEATURE_QUERY_KEYS.listPrefix() })
    },
  })
}

export function useUpdateTicketFeatureMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateTicketFeaturePayload }) =>
      updateTicketFeature(id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TICKET_FEATURE_QUERY_KEYS.listPrefix() })
    },
  })
}

export function useToggleTicketFeatureActiveMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      toggleTicketFeatureActive(id, isActive),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TICKET_FEATURE_QUERY_KEYS.listPrefix() })
    },
  })
}
